import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Search, FileDown, Package, Euro } from "lucide-react";
import { tr } from "../contexts/LangContext";

/* ====================== Shared Orders panel (RYCM iter 23) ======================
 * Boutique orders (vêtements, accessoires, événements). Stage products are
 * handled separately by StageRegistrationsPanel.
 * RBAC matches stage view (admin / secretary / treasurer / president read-only).
 * Used in: /admin (already had a view), /treasury, /secretariat (3c).
 * ============================================================================ */
export default function OrdersPanel({ embedded = false }) {
  const { user } = useAuth();
  const userRoles = useMemo(() => {
    const r = new Set();
    if (user?.role) r.add(user.role);
    (user?.roles || []).forEach((x) => r.add(x));
    return r;
  }, [user]);
  const canMarkPaid = userRoles.has("admin") || userRoles.has("treasurer");

  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [groupByProduct, setGroupByProduct] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [user?.id]);

  // Flatten orders → line items. Stages are now INCLUDED (RYCM iter 26):
  // pending stage orders (e.g. bank_transfer awaiting payment) must be visible to
  // the treasurer somewhere, since the stage_registrations collection is only filled
  // at fulfillment. Once paid → registrations are created and the row still appears here.
  const lines = useMemo(() => {
    const out = [];
    orders.forEach((o) => {
      (o.items || []).forEach((it, idx) => {
        out.push({ ...it, _order: o, _key: `${o.id}-${idx}` });
      });
    });
    return out;
  }, [orders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lines.filter((it) => {
      const o = it._order;
      if (statusFilter !== "all" && (o.status || "pending") !== statusFilter) return false;
      if (!q) return true;
      const hay = [it.name, o.customer_name, o.customer_email, o.customer_phone].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [lines, search, statusFilter]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((it) => {
      const k = it.name || tr("Produit inconnu");
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const totalRevenue = useMemo(() =>
    filtered.reduce((s, it) => s + (it.line_total || 0), 0)
  , [filtered]);

  const exportXlsx = () => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backend}/api/admin/orders/export.xlsx`, "_blank");
  };

  const markPaid = async (orderId) => {
    if (!canMarkPaid) return;
    if (!window.confirm(tr("Marquer cette commande comme payée ?"))) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${orderId}/mark-paid`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const STATUS_OPTS = ["pending", "paid", "cancelled", "refunded"];
  const statusBadge = (s) => ({
    paid:      "bg-emerald-100 text-emerald-800 border-emerald-200",
    pending:   "bg-amber-100   text-amber-800   border-amber-200",
    cancelled: "bg-slate-100   text-slate-700   border-slate-200",
    refunded:  "bg-red-100     text-red-800     border-red-200",
  }[s || "pending"]);

  return (
    <div className={embedded ? "" : "pt-24 md:pt-28 pb-16 min-h-screen bg-sand"} data-testid="orders-panel">
      <div className={embedded ? "" : "max-w-7xl mx-auto px-4 md:px-10 lg:px-12"}>
        {!embedded && (
          <>
            <div className="label-eyebrow text-club-red mb-3">{tr("Commandes boutique")}</div>
            <h1 className="font-serif text-4xl text-navy">{tr("Commandes")}</h1>
            <div className="divider-gold mt-5 mb-8" />
          </>
        )}

        {/* Summary banner */}
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="orders-summary">
          <Stat icon={Package} label={tr("Articles")} value={filtered.length} accent="bg-navy text-white" />
          <Stat icon={Package} label={tr("Commandes")} value={new Set(filtered.map((l) => l._order.id)).size} accent="bg-club-yellow text-navy" />
          <Stat icon={Euro} label={tr("Total brut")} value={`€ ${totalRevenue.toFixed(2)}`} accent="bg-emerald-600 text-white" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="orders-toolbar">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input placeholder={tr("Rechercher (produit, client, e-mail…)")} value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full border border-slate-300 px-3 py-2 outline-none focus:border-navy"
              data-testid="orders-search" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-slate-300 px-3 py-2 text-sm" data-testid="orders-status-filter">
            <option value="all">{tr("Tous statuts")}</option>
            {STATUS_OPTS.map((s) => <option key={s} value={s}>{({pending: tr("En attente"), paid: tr("Payé"), cancelled: tr("Annulée"), refunded: tr("Remboursée")})[s] || s}</option>)}
          </select>
          <button onClick={() => setGroupByProduct((v) => !v)}
            className={`px-3 py-2 text-xs uppercase tracking-widest border ${groupByProduct ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-300 hover:border-navy"}`}
            data-testid="orders-group-toggle">
            {groupByProduct ? tr("Vue groupée") : tr("Vue liste")}
          </button>
          <button onClick={exportXlsx}
            className="px-3 py-2 text-xs uppercase tracking-widest border border-club-yellow-dark bg-club-yellow text-navy hover:bg-club-yellow-dark"
            data-testid="orders-export-all">
            <FileDown className="w-3 h-3 inline mr-1" /> {tr("Exporter tout (XLSX)")}
          </button>
        </div>

        {/* Table */}
        {groupByProduct ? (
          <div className="space-y-6">
            {groups.length === 0 && (
              <div className="bg-white border border-slate-200 text-center text-slate-500 py-8" data-testid="orders-empty">
                {tr("Aucune commande pour les filtres actuels.")}
              </div>
            )}
            {groups.map(([title, rows]) => {
              const sub = rows.reduce((s, r) => s + (r.line_total || 0), 0);
              return (
                <div key={title} className="bg-white border border-slate-200" data-testid={`order-group-${title}`}>
                  <div className="px-4 py-3 bg-sand border-b border-slate-200 flex items-center justify-between">
                    <div>
                      <div className="font-serif text-navy text-base">{title}</div>
                      <div className="text-[11px] uppercase tracking-widest text-slate-500">
                        {rows.length} {rows.length > 1 ? tr("lignes") : tr("ligne")} · {rows.reduce((s, r) => s + (r.quantity || 0), 0)} {rows.reduce((s, r) => s + (r.quantity || 0), 0) > 1 ? tr("pièces") : tr("pièce")}
                      </div>
                    </div>
                    <div className="font-serif text-navy">€ {sub.toFixed(2)}</div>
                  </div>
                  <OrdersTable rows={rows} canMarkPaid={canMarkPaid} statusBadge={statusBadge} markPaid={markPaid} busy={busy} />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <OrdersTable rows={filtered} canMarkPaid={canMarkPaid} statusBadge={statusBadge} markPaid={markPaid} busy={busy} />
          </div>
        )}
        <div className="text-[11px] text-slate-500 mt-3">{filtered.length} ligne(s) — sur {lines.length} au total.</div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`px-5 py-4 ${accent} flex items-center gap-3`}>
      <Icon className="w-5 h-5" strokeWidth={1.5} />
      <div>
        <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
        <div className="font-serif text-2xl">{value}</div>
      </div>
    </div>
  );
}

function OrdersTable({ rows, canMarkPaid, statusBadge, markPaid, busy }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-sand">
        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-600 border-b border-slate-200">
          <th className="px-3 py-3">{tr("Date")}</th>
          <th className="px-3">{tr("Client")}</th>
          <th className="px-3">{tr("Produit")}</th>
          <th className="px-3">{tr("Taille / Couleur")}</th>
          <th className="px-3 text-right">{tr("Qté")}</th>
          <th className="px-3 text-right">{tr("PU")}</th>
          <th className="px-3 text-right">Total</th>
          <th className="px-3">{tr("Statut")}</th>
          <th className="px-3">{tr("Actions")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (<tr><td colSpan={9} className="text-center text-slate-500 py-6">{tr("Aucune ligne.")}</td></tr>)}
        {rows.map((it) => {
          const o = it._order;
          return (
            <tr key={it._key} className="border-b border-slate-100 hover:bg-sand/40" data-testid={`order-row-${it._key}`}>
              <td className="px-3 py-3 align-top text-[12px] text-slate-600">{(o.created_at || "").slice(0, 10)}</td>
              <td className="px-3 align-top text-[12px]">
                <div className="text-navy">{o.customer_name || "—"}</div>
                <div className="text-slate-500">{o.customer_email}</div>
              </td>
              <td className="px-3 align-top text-navy">{it.name}</td>
              <td className="px-3 align-top text-[12px] text-slate-700">
                {it.size && <span className="bg-slate-100 px-2 py-0.5 mr-1 text-[10px] uppercase">{it.size}</span>}
                {it.color && <span className="bg-slate-100 px-2 py-0.5 text-[10px] uppercase">{it.color}</span>}
                {!it.size && !it.color && "—"}
              </td>
              <td className="px-3 align-top text-right">{it.quantity}</td>
              <td className="px-3 align-top text-right">€ {Number(it.unit_price || 0).toFixed(2)}</td>
              <td className="px-3 align-top text-right font-semibold">€ {Number(it.line_total || 0).toFixed(2)}</td>
              <td className="px-3 align-top">
                <span className={`text-[11px] uppercase tracking-widest border px-2 py-1 inline-block ${statusBadge(o.status)}`}>
                  {o.status || "pending"}
                </span>
              </td>
              <td className="px-3 align-top whitespace-nowrap">
                {canMarkPaid && o.status !== "paid" && (
                  <button
                    disabled={busy}
                    onClick={() => markPaid(o.id)}
                    className="text-[11px] uppercase tracking-widest text-emerald-700 border border-emerald-700 px-2 py-1 hover:bg-emerald-700 hover:text-white"
                    data-testid={`order-markpaid-${o.id}`}
                    title={tr("Marquer la commande comme payée")}
                  >{tr("Marquer payé")}</button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
